/**
 * Musical section definitions for the harmony system.
 * Each instrument belongs to one orchestral SECTION that determines
 * its musical behavior (what patterns it plays).
 *
 * Sections:
 *   bass        – plays chord roots, walking bass lines (1-2 notes/bar)
 *   rhythm      – comps chords on upbeats, rhythmic strumming (4-8 notes/bar)
 *   harmony     – sustains chord tones as pads (1-2 notes/bar, long durations)
 *   melody      – plays the main singable melody line (4-8 notes/bar)
 *   counter     – answers the melody with countermelody (3-6 notes/bar)
 *   percussion  – keeps steady rhythmic pulse
 */

export const ORCHESTRA_SECTIONS = {
  bass:       { weight: 1, octaveRange: [-1, 0],  volumeDb: -10, noteDuration: [0.4, 1.2] },
  rhythm:     { weight: 2, octaveRange: [0, 1],   volumeDb: -14, noteDuration: [0.15, 0.4] },
  harmony:    { weight: 2, octaveRange: [0, 1],   volumeDb: -18, noteDuration: [1.0, 2.5] },
  melody:     { weight: 2, octaveRange: [1, 2],   volumeDb: -8,  noteDuration: [0.1, 0.5] },
  counter:    { weight: 2, octaveRange: [0, 1],   volumeDb: -12, noteDuration: [0.15, 0.6] },
  percussion: { weight: 1, octaveRange: [-1, 0],  volumeDb: -14, noteDuration: [0.03, 0.12] },
  atmosphere: { weight: 1, octaveRange: [1, 2],   volumeDb: -20, noteDuration: [0.8, 2.0] },
};

/**
 * Instrument → section mapping.
 * Each instrument is tagged with its orchestral section.
 */
export const INSTRUMENT_SECTION_MAP = {
  'bass':       'bass',
  'percussion': 'percussion',
  'pad':        'harmony',
  'keys':       'rhythm',
  'pluck':      'rhythm',
  'lead':       'melody',
  'arp':        'counter',
  'atmosphere': 'atmosphere',
};

/**
 * Instruments grouped by section for easy picking during creature generation.
 */
export const SECTION_INSTRUMENTS = {};
for (const [instId, section] of Object.entries(INSTRUMENT_SECTION_MAP)) {
  if (!SECTION_INSTRUMENTS[section]) SECTION_INSTRUMENTS[section] = [];
  SECTION_INSTRUMENTS[section].push(instId);
}

/**
 * The order in which sections should be filled when assigning species.
 * Ensures every planet has a musically complete foundation.
 * First pass: one of each essential section.
 * After that: extras are distributed by weight.
 */
export const SECTION_FILL_ORDER = [
  'bass', 'percussion', 'harmony', 'melody', 'rhythm', 'counter', 'atmosphere',
];

/**
 * Genre-specific section fill orders and weights.
 * Controls which orchestral sections dominate for each genre.
 *
 * fillOrder: priority order for first-pass species assignment
 * extraWeights: weighted pool for additional species beyond first pass
 * sectionOmit: sections that may be skipped entirely for this genre
 */
export const GENRE_SECTION_CONFIG = {
  orchestral: {
    fillOrder: ['bass', 'percussion', 'harmony', 'melody', 'rhythm', 'counter', 'atmosphere'],
    extraWeights: { melody: 3, harmony: 2, counter: 2, rhythm: 2, bass: 1, percussion: 1, atmosphere: 1 },
  },
  ambient: {
    fillOrder: ['harmony', 'atmosphere', 'bass', 'melody'],
    extraWeights: { harmony: 4, atmosphere: 3, melody: 2, bass: 1, counter: 1 },
    sectionOmit: ['percussion'],
  },
  jazz: {
    fillOrder: ['bass', 'percussion', 'melody', 'harmony', 'rhythm', 'counter'],
    extraWeights: { melody: 3, counter: 3, rhythm: 2, harmony: 2, bass: 1, percussion: 1 },
  },
  breakcore: {
    fillOrder: ['percussion', 'bass', 'melody'],
    extraWeights: { percussion: 5, bass: 2, melody: 1 },
    sectionOmit: ['harmony', 'counter', 'atmosphere'],
  },
  electronic: {
    fillOrder: ['percussion', 'bass', 'melody', 'harmony', 'rhythm'],
    extraWeights: { percussion: 2, bass: 2, melody: 2, harmony: 1, rhythm: 2 },
  },
  minimal: {
    fillOrder: ['percussion', 'bass', 'melody'],
    extraWeights: { percussion: 2, bass: 2, melody: 3 },
    sectionOmit: ['counter', 'atmosphere'],
  },
  folkAcoustic: {
    fillOrder: ['rhythm', 'bass', 'melody', 'percussion', 'harmony'],
    extraWeights: { rhythm: 3, melody: 3, bass: 1, harmony: 1, counter: 1 },
  },
  cinematic: {
    fillOrder: ['harmony', 'atmosphere', 'bass', 'melody', 'counter'],
    extraWeights: { harmony: 4, atmosphere: 3, melody: 3, counter: 2, bass: 2, percussion: 1 },
  },
  lofi: {
    fillOrder: ['percussion', 'bass', 'harmony', 'melody', 'rhythm'],
    extraWeights: { melody: 3, harmony: 2, rhythm: 2, bass: 1, percussion: 1, atmosphere: 1 },
  },
  world: {
    fillOrder: ['percussion', 'bass', 'melody', 'rhythm', 'harmony'],
    extraWeights: { percussion: 3, melody: 2, rhythm: 2, bass: 1, harmony: 1, counter: 1 },
  },
};
